import { useState, useEffect } from 'react';

export interface SeriesProgress {
  completedRounds: number;
  totalRounds: number;
  lastActivity: string;
  averageScore: number;
  roundScores: Record<string, number>; // Map of round IDs to scores
}

export interface SeriesProgressMap {
  [seriesId: string]: SeriesProgress;
}

/**
 * Hook to manage progress data for interview series
 * This is a temporary solution until API endpoints are implemented
 */
export const useSeriesProgress = (seriesId?: string) => {
  // State to track all series progress
  const [progressMap, setProgressMap] = useState<SeriesProgressMap>({});
  
  // Load progress data from localStorage on mount
  useEffect(() => {
    const storedProgress = localStorage.getItem('series_progress');
    if (storedProgress) {
      try {
        const parsed = JSON.parse(storedProgress);
        if (parsed && typeof parsed === 'object') {
          setProgressMap(parsed);
        }
      } catch (e) {
        console.error('Error parsing series progress from localStorage:', e);
      }
    }
  }, []);
  
  /**
   * Get progress for a specific series
   * @param id Series ID to get progress for
   * @returns Progress data or null if not found
   */
  const getProgress = (id: string): SeriesProgress | null => {
    return progressMap[id] || null;
  };
  
  /**
   * Initialize progress for a series (called when user enrolls)
   * @param id Series ID to initialize progress for
   * @param totalRounds Total number of rounds in the series
   * @returns boolean indicating success
   */
  const initializeProgress = (id: string, totalRounds: number): boolean => {
    if (progressMap[id]) return true; // Already initialized
    
    try {
      // Create initial progress
      const initialProgress: SeriesProgress = {
        completedRounds: 0,
        totalRounds,
        lastActivity: new Date().toISOString(),
        averageScore: 0,
        roundScores: {},
      };
      
      // Update state
      const updatedProgress = {
        ...progressMap,
        [id]: initialProgress
      };
      
      setProgressMap(updatedProgress);
      
      // Persist to localStorage
      localStorage.setItem('series_progress', JSON.stringify(updatedProgress));
      return true;
    } catch (e) {
      console.error('Error initializing series progress:', e);
      return false;
    }
  };
  
  /**
   * Update progress for a series when a round is completed
   * @param id Series ID to update progress for
   * @param roundId Round ID that was completed
   * @param score Score achieved in the round
   * @returns boolean indicating success
   */
  const updateRoundProgress = (id: string, roundId: string, score: number): boolean => {
    try {
      // Get current progress
      const currentProgress = progressMap[id];
      if (!currentProgress) {
        console.error('Cannot update progress: series not initialized');
        return false;
      }
      
      // Update round scores
      const updatedRoundScores = {
        ...currentProgress.roundScores,
        [roundId]: score
      };
      
      // Calculate new average score
      const scores = Object.values(updatedRoundScores);
      const newAverageScore = scores.length > 0 
        ? scores.reduce((sum, s) => sum + s, 0) / scores.length 
        : 0;
      
      // Count completed rounds
      const completedRounds = Object.keys(updatedRoundScores).length;
      
      // Create updated progress
      const updatedProgress: SeriesProgress = {
        ...currentProgress,
        completedRounds,
        lastActivity: new Date().toISOString(),
        averageScore: newAverageScore,
        roundScores: updatedRoundScores
      };
      
      // Update state
      const updatedMap = {
        ...progressMap,
        [id]: updatedProgress
      };
      
      setProgressMap(updatedMap);
      
      // Persist to localStorage
      localStorage.setItem('series_progress', JSON.stringify(updatedMap));
      return true;
    } catch (e) {
      console.error('Error updating round progress:', e);
      return false;
    }
  };
  
  /**
   * Reset progress for a series
   * @param id Series ID to reset progress for
   * @returns boolean indicating success
   */
  const resetProgress = (id: string): boolean => {
    try {
      // Get current progress
      const currentProgress = progressMap[id];
      if (!currentProgress) return true; // Nothing to reset
      
      // Create reset progress
      const resetProgressData: SeriesProgress = {
        ...currentProgress,
        completedRounds: 0,
        lastActivity: new Date().toISOString(),
        averageScore: 0,
        roundScores: {}
      };
      
      // Update state
      const updatedMap = {
        ...progressMap,
        [id]: resetProgressData
      };
      
      setProgressMap(updatedMap);
      
      // Persist to localStorage
      localStorage.setItem('series_progress', JSON.stringify(updatedMap));
      return true;
    } catch (e) {
      console.error('Error resetting series progress:', e);
      return false;
    }
  };
  
  // Get current series progress if seriesId is provided
  const currentProgress = seriesId ? getProgress(seriesId) : null;
  
  return {
    progressMap,
    currentProgress,
    getProgress,
    initializeProgress,
    updateRoundProgress,
    resetProgress
  };
};

export default useSeriesProgress; 